import { People, EyeVisible, Calendar } from '@factorialco/f0-react/icons/app'
import { FeedbackEntryFragment } from 'generated/resources/graphql'
import buildUrl from 'modules/core/lib/buildUrl'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

type Mode = 'received' | 'given'

function receiverNames(receivers: FeedbackEntryFragment['receivers']): string {
  if (receivers.length === 0) return i18n.t('common.unknown')

  const names = receivers.map((r) => r.fullName)
  if (names.length <= 2) return names.join(', ')

  const othersLabel = i18n.tp('performance_feedbacks.feedback_entry.other', {
    count: names.length - 2,
  })
  return `${names[0]}, ${names[1]} + ${othersLabel}`
}

function authorName(author: FeedbackEntryFragment['author']): string {
  return author?.fullName ?? i18n.t('common.unknown')
}

export function cardVisualizations(mode: Mode) {
  return [
    {
      type: 'card' as const,
      options: {
        title: (entry: FeedbackEntryFragment) =>
          mode === 'received'
            ? i18n.t('performance_feedbacks.feedback_entry.from', {
                name: authorName(entry.author),
              })
            : i18n.t('performance_feedbacks.feedback_entry.to', {
                names: receiverNames(entry.receivers),
              }),
        description: (entry: FeedbackEntryFragment) => entry.feedback,
        cardProperties: [
          {
            label: i18n.t('performance_feedbacks.feedback_entry.sent_to'),
            icon: People,
            render: (entry: FeedbackEntryFragment) => ({
              type: 'avatarList' as const,
              value: {
                avatarList: entry.receivers.map((r) => ({
                  type: 'person' as const,
                  firstName: r.firstName,
                  lastName: r.lastName,
                  src: buildUrl(r.avatar),
                })),
              },
            }),
          },
          {
            label: i18n.t('performance_feedbacks.feedback_entry.date_label'),
            icon: Calendar,
            render: (entry: FeedbackEntryFragment) =>
              convertDate(entry.createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
          },
          {
            label: i18n.t('performance_feedbacks.feedback_entry.visibility_label'),
            icon: EyeVisible,
            render: (entry: FeedbackEntryFragment) => {
              switch (entry.visibility) {
                case 'private':
                  return {
                    type: 'status' as const,
                    value: {
                      status: 'neutral',
                      label: i18n.t('performance_feedbacks.feedback_entry.private'),
                    },
                  }
                case 'private_and_manager':
                  return {
                    type: 'status' as const,
                    value: {
                      status: 'neutral',
                      label: i18n.t('performance_feedbacks.feedback_entry.private_and_manager'),
                    },
                  }
                case 'manager_only':
                  return {
                    type: 'status' as const,
                    value: {
                      status: 'neutral',
                      label: i18n.t('performance_feedbacks.feedback_entry.manager_only'),
                    },
                  }
                default:
                  return {
                    type: 'status' as const,
                    value: {
                      status: 'neutral',
                      label: entry.visibility,
                    },
                  }
              }
            },
          },
        ],
      },
    },
  ]
}
